// Platform storage abstraction. Web/extension use localStorage (the default);
// React Native injects a synchronous adapter (MMKV) via setStorageAdapter()
// before the app renders. The interface is deliberately synchronous — the
// offline managers and API client read tokens/state inline.
export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// In-memory fallback for environments with no localStorage (RN before the
// adapter is injected, tests, SSR). Data does not persist.
const memoryStore = new Map<string, string>();
const memoryAdapter: StorageAdapter = {
  getItem: (key) => memoryStore.get(key) ?? null,
  setItem: (key, value) => {
    memoryStore.set(key, value);
  },
  removeItem: (key) => {
    memoryStore.delete(key);
  },
};

let adapter: StorageAdapter =
  typeof localStorage !== 'undefined' ? localStorage : memoryAdapter;

export const setStorageAdapter = (custom: StorageAdapter): void => {
  adapter = custom;
};

// All access goes through here so a quota error / restricted context can
// never throw into feature code.
export const storage: StorageAdapter = {
  getItem: (key) => {
    try {
      return adapter.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      adapter.setItem(key, value);
    } catch {}
  },
  removeItem: (key) => {
    try {
      adapter.removeItem(key);
    } catch {}
  },
};
